import type { DealSection } from './types';

/**
 * Dragging a section above or below another one.
 *
 * Kyu, 10 September: "I want to make all sections, whether it's in corporate
 * gifting or partnerships, movable. Like I can drag this section up or down so
 * I can reorganize them depending on what we need for the meantime."
 *
 * Pure, so it can be tested without a board. Given the sections as displayed,
 * the one being dragged and the one it was dropped on, it returns the new
 * order and ONLY the rows whose position actually changed, so a drop that
 * lands where it started sends nothing to the database.
 *
 * Positions are re-spaced to 10, 20, 30… on every move rather than nudged
 * between neighbours. Nudging runs out of room after a few moves and the two
 * numbers collide, at which point the order is whatever Postgres feels like.
 * Rewriting a handful of small integers is cheaper than that ever being true.
 */

/** The dataTransfer type a section drag carries, so it cannot be mistaken for a deal. */
export const SECTION_DRAG_TYPE = 'application/x-deal-section';

export interface PositionChange {
  id: string;
  position: number;
}

export function reorderSections(
  sections: readonly DealSection[],
  draggedId: string,
  droppedOnId: string,
): { order: DealSection[]; changes: PositionChange[] } {
  if (draggedId === droppedOnId) {
    return { order: [...sections], changes: [] };
  }
  const from = sections.findIndex((section) => section.id === draggedId);
  const to = sections.findIndex((section) => section.id === droppedOnId);
  if (from === -1 || to === -1) {
    return { order: [...sections], changes: [] };
  }

  const order = [...sections];
  const [moved] = order.splice(from, 1);
  if (moved === undefined) {
    return { order: [...sections], changes: [] };
  }
  order.splice(to, 0, moved);

  const changes: PositionChange[] = [];
  const respaced = order.map((section, index) => {
    const position = (index + 1) * 10;
    if (section.position !== position) {
      changes.push({ id: section.id, position });
    }
    return { ...section, position };
  });
  return { order: respaced, changes };
}
